import { createTheme } from '@mui/material/styles';

/**
 * Material-UI theme configuration
 * Defines consistent colors, typography, spacing, and component overrides
 */
const theme = createTheme({
  palette: {
    primary: {
      main: '#1A7A50',
      light: '#A7F3D0',
      dark: '#0A2E1F',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D4A843',
      light: '#FDE68A',
      dark: '#92400E',
      contrastText: '#0A2E1F',
    },
    success: {
      main: '#2ECC7A',
      light: '#A7F3D0',
      dark: '#0F4D33',
    },
    warning: {
      main: '#F59E0B',
      light: '#FDE68A',
      dark: '#92400E',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828',
    },
    info: {
      main: '#0EA5E9',
      light: '#BAE6FD',
      dark: '#0369A1',
    },
    background: {
      default: '#FAFAF5',
      paper: '#FFFFFF',
    },
    divider: 'rgba(10,46,31,0.10)',
    text: {
      primary: '#0A2E1F',
      secondary: '#6B8A7A',
      disabled: '#BDBDBD',
    },
  },

  typography: {
    fontFamily: ['"DM Sans"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '0px',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.43,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '1rem',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
  },

  spacing: 8,

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.08)',
    '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.12)',
    '0px 12px 24px rgba(0, 0, 0, 0.14)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.17)',
    '0px 28px 56px rgba(0, 0, 0, 0.18)',
    '0px 32px 64px rgba(0, 0, 0, 0.19)',
    '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '0px 8px 16px rgba(0, 0, 0, 0.12)',
    '0px 12px 24px rgba(0, 0, 0, 0.14)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.16)',
    '0px 24px 48px rgba(0, 0, 0, 0.17)',
    '0px 28px 56px rgba(0, 0, 0, 0.18)',
    '0px 32px 64px rgba(0, 0, 0, 0.19)',
    '0px 36px 72px rgba(0, 0, 0, 0.2)',
    '0px 40px 80px rgba(0, 0, 0, 0.21)',
    // MUI requires exactly 25 shadow levels (0–24). The array above stopped at 20,
    // so every <Dialog> (elevation 24) logged a console error and rendered shadowless.
    '0px 42px 84px rgba(0, 0, 0, 0.22)',
    '0px 44px 88px rgba(0, 0, 0, 0.22)',
    '0px 46px 92px rgba(0, 0, 0, 0.23)',
    '0px 48px 96px rgba(0, 0, 0, 0.23)',
    '0px 50px 100px rgba(0, 0, 0, 0.24)',
  ],

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
        },
        containedPrimary: {
          '&:hover': {
            boxShadow: '0px 8px 32px rgba(26, 122, 80, 0.35)',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            transition: 'all 0.2s ease',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 3,
          borderRadius: 999,
        },
        scrollButtons: {
          borderRadius: 12,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          paddingLeft: 14,
          paddingRight: 14,
          borderRadius: 999,
          marginRight: 6,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          backgroundColor: 'rgba(10,46,31,0.05)',
          color: '#0A2E1F',
        },
      },
    },
  },
});

export default theme;
